import FormattedMessage from 'I18n/components/FormattedMessage';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Text } from 'foundations-components/components/Text';
import { List as ImmutableList, OrderedMap } from 'immutable';
import { useCallback, useMemo } from 'react';
import { List } from 'react-virtualized';
import { isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { getDisplayParamsDataColumns } from 'reporting-data/report/configReportGetters';
import useFetchPropertyOptionsForDataColumnsOptionModal from './useFetchPropertyOptionsForDataColumnsOptionModal';
import { getPropertiesFromConfigReportForDataColumnsModal, isUEQLSearchConfig } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
const ROW_WIDTH = 420;
const ROW_HEIGHT = 32;
const LIST_HEIGHT = 304;
const OVERSCAN_ROW_COUNT = 50;
const VirtualizedPropertyCheckboxList = ({
  report,
  onChange,
  groupedOptions
}) => {
  const propertiesInOriginalReport = getPropertiesFromConfigReportForDataColumnsModal(report);
  const dataColumnsFromReport = useMemo(() => {
    var _getDisplayParamsData, _getDisplayParamsData2;
    return ((_getDisplayParamsData = getDisplayParamsDataColumns(report)) === null || _getDisplayParamsData === void 0 || (_getDisplayParamsData2 = _getDisplayParamsData.toJS) === null || _getDisplayParamsData2 === void 0 ? void 0 : _getDisplayParamsData2.call(_getDisplayParamsData)) || [];
  }, [report]);
  const reportIsUEQLSearchConfig = isUEQLSearchConfig(report);
  const onCheckboxClick = useCallback(propertyValue => ({
    target: {
      checked
    }
  }) => {
    if (!checked) {
      const index = dataColumnsFromReport.indexOf(propertyValue);
      const updatedReport = report.deleteIn(['displayParams', 'dataColumns', index]);
      if (reportIsUEQLSearchConfig) {
        const metricIndex = updatedReport.getIn(['config', 'metrics']).findIndex(metric => metric.get('property') === propertyValue);
        onChange(updatedReport.deleteIn(['config', 'metrics', metricIndex]));
      } else {
        onChange(updatedReport);
      }
    } else {
      const updatedReport = report.updateIn(['displayParams', 'dataColumns'], ImmutableList([]), columns => columns.push(propertyValue));
      if (reportIsUEQLSearchConfig) {
        onChange(updatedReport.updateIn(['config', 'metrics'], ImmutableList([]), metrics => metrics.push(OrderedMap({
          property: propertyValue,
          metricTypes: ImmutableList([]),
          percentiles: ImmutableList([])
        }))));
      } else {
        onChange(updatedReport);
      }
    }
  }, [dataColumnsFromReport, onChange, report, reportIsUEQLSearchConfig]);
  const flattenedOptions = useMemo(() => {
    const flattened = groupedOptions.map(({
      options,
      text
    }) => {
      return [text, ...options];
    }).flat();
    if (flattened[0] === '') {
      flattened.shift();
    }
    return flattened;
  }, [groupedOptions]);
  const renderRow = ({
    index,
    key,
    style
  }) => {
    const option = flattenedOptions[index];
    if (typeof option === 'string') {
      return (
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        // The style attribute is required by react-virtualized for positioning and should not be problematic for the migration
        _jsx("div", {
          style: style,
          children: /*#__PURE__*/_jsx(Text, {
            typography: "heading-100",
            marginBlock: "100",
            children: option
          })
        }, key)
      );
    }
    const isDisabled = reportIsUEQLSearchConfig ? propertiesInOriginalReport.includes(option.value) && !dataColumnsFromReport.includes(option.value) : propertiesInOriginalReport.includes(option.value);
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      // The style attribute is required by react-virtualized for positioning and should not be problematic for the migration
      _jsx("div", {
        style: style,
        children: /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isDisabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "report-customization-components.customization-components.data-columns-modal.config.disabledProperty"
          }),
          placement: "top",
          children: /*#__PURE__*/_jsx(Checkbox, {
            onChange: onCheckboxClick(option.value),
            checked: dataColumnsFromReport.includes(option.value),
            disabled: isDisabled,
            children: /*#__PURE__*/_jsx(TruncateString, {
              children: option.text
            })
          })
        })
      }, key)
    );
  };
  return /*#__PURE__*/_jsx(List, {
    width: ROW_WIDTH,
    height: LIST_HEIGHT,
    rowHeight: ROW_HEIGHT,
    rowRenderer: renderRow,
    rowCount: flattenedOptions.length,
    overscanRowCount: OVERSCAN_ROW_COUNT
  });
};
const PropertyOptionsPanel = ({
  onChange,
  report,
  searchValue
}) => {
  const propertyOptionsFetch = useFetchPropertyOptionsForDataColumnsOptionModal(report);
  if (isLoading(propertyOptionsFetch)) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      "data-test-id": "properties-loading",
      layout: "centered"
    });
  } else if (isSucceeded(propertyOptionsFetch)) {
    const propertyGroupOptions = propertyOptionsFetch.data;
    const filteredPropertyOptions = propertyGroupOptions.map(group => {
      const filteredOptions = group.options.filter(option => {
        var _option$text$toLowerC, _option$text, _option$text$toLowerC2, _option$text$toLowerC3;
        return !searchValue || searchValue.length < 2 || ((_option$text$toLowerC = (_option$text = option.text).toLowerCase) === null || _option$text$toLowerC === void 0 || (_option$text$toLowerC2 = (_option$text$toLowerC3 = _option$text$toLowerC.call(_option$text)).includes) === null || _option$text$toLowerC2 === void 0 ? void 0 : _option$text$toLowerC2.call(_option$text$toLowerC3, searchValue.toLowerCase()));
      });
      return Object.assign({}, group, {
        options: filteredOptions
      });
    }).filter(group => {
      return !!group.options.length;
    });
    return /*#__PURE__*/_jsx(VirtualizedPropertyCheckboxList, {
      report: report,
      onChange: onChange,
      groupedOptions: filteredPropertyOptions
    });
  } else {
    return /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "report-customization-components.customization-components.data-columns-modal.config.error"
      })
    });
  }
};
export default PropertyOptionsPanel;